import dayjs from 'dayjs';
import z from 'zod';
import { HumanFullNameSchema, StringBooleanSchema, UnixTimestampSchema } from '../common/schemas';
import { UserIdSchema } from '../users/schemas';
import { UserNoteAssignee, UserNoteCreator, UserNoteType } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBUserNoteTypeSchema = z.enum(UserNoteType);

/**
 * DB Schemas
 */

export const DBUserNoteUserIdSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    DBUserNoteTypeSchema.safeParse(parts[0]).success &&
    z.lazy(() => UserIdSchema).safeParse(parts[1]).success
  );
});
export const DBUserNoteCreationDatetimeSchema = UnixTimestampSchema;
export const DBUserNoteActiveSchema = StringBooleanSchema;
export const DBUserNoteActiveBillingRelatedKeySchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    StringBooleanSchema.safeParse(parts[0]).success &&
    StringBooleanSchema.safeParse(parts[1]).success
  );
});
export const DBUserNoteAssigneeSchema = z.union([
  z.enum(UserNoteAssignee),
  z.lazy(() => UserIdSchema),
]);
export const DBUserNoteAssigneeNameSchema = z.union([z.literal(''), HumanFullNameSchema]);
export const DBUserNoteBillingRelatedSchema = z.boolean();
export const DBUserNoteCreatorSchema = z.union([
  z.enum(UserNoteCreator),
  z.lazy(() => UserIdSchema),
]);
export const DBUserNoteCreatorNameSchema = z.union([
  z.string().min(1).max(256),
  HumanFullNameSchema,
]);
export const DBUserNoteDueDatetimeSchema = UnixTimestampSchema;
export const DBUserNoteLastEditDatetimeSchema = UnixTimestampSchema;
export const DBUserNoteNoteSchema = z.string().min(1).max(4096);
export const DBUserNotePriorityDatetimeSchema = UnixTimestampSchema;

export const DBUserNoteSchema = z.object({
  userId: DBUserNoteUserIdSchema,
  creationDatetime: DBUserNoteCreationDatetimeSchema,
  active: DBUserNoteActiveSchema,
  activeBillingRelatedKey: DBUserNoteActiveBillingRelatedKeySchema,
  assignee: DBUserNoteAssigneeSchema,
  assigneeName: DBUserNoteAssigneeNameSchema,
  billingRelated: DBUserNoteBillingRelatedSchema,
  creator: DBUserNoteCreatorSchema,
  creatorName: DBUserNoteCreatorNameSchema,
  dueDatetime: DBUserNoteDueDatetimeSchema,
  lastEditDatetime: DBUserNoteLastEditDatetimeSchema,
  note: DBUserNoteNoteSchema,
  priorityDatetime: DBUserNotePriorityDatetimeSchema,
});

/**
 * Client Schemas
 */

export const UserNoteUserIdSchema = z.lazy(() => UserIdSchema);
export const UserNoteTypeSchema = DBUserNoteTypeSchema;
export const UserNoteCreationDatetimeSchema = DBUserNoteCreationDatetimeSchema;
export const UserNoteActiveSchema = z.boolean();
export const UserNoteAssigneeSchema = DBUserNoteAssigneeSchema;
export const UserNoteAssigneeNameSchema = DBUserNoteAssigneeNameSchema;
export const UserNoteBillingRelatedSchema = DBUserNoteBillingRelatedSchema;
export const UserNoteCreatorSchema = DBUserNoteCreatorSchema;
export const UserNoteCreatorNameSchema = DBUserNoteCreatorNameSchema;
export const UserNoteDueDatetimeSchema = DBUserNoteDueDatetimeSchema;
export const UserNoteLastEditDatetimeSchema = DBUserNoteLastEditDatetimeSchema;
export const UserNoteNoteSchema = DBUserNoteNoteSchema;
export const UserNotePriorityDatetimeSchema = DBUserNotePriorityDatetimeSchema;

export const UserNoteSchema = z.object({
  userId: UserNoteUserIdSchema.meta({ title: 'User ID' }),
  type: UserNoteTypeSchema,
  creationDatetime: UserNoteCreationDatetimeSchema.default(() => dayjs().valueOf()).meta({
    title: 'Created at',
  }),
  active: UserNoteActiveSchema.default(true).meta({ title: 'Active' }),
  assignee: UserNoteAssigneeSchema.default(UserNoteAssignee.Unassigned).meta({
    title: 'Assignee',
  }),
  assigneeName: UserNoteAssigneeNameSchema.default('').meta({ title: 'Assignee name' }),
  billingRelated: UserNoteBillingRelatedSchema.default(false).meta({ title: 'Billing related' }),
  creator: UserNoteCreatorSchema.meta({ title: 'Creator' }),
  creatorName: UserNoteCreatorNameSchema.meta({ title: 'Creator name' }),
  dueDatetime: UserNoteDueDatetimeSchema.default(0).meta({ title: 'Due at' }),
  lastEditDatetime: UserNoteLastEditDatetimeSchema.default(() => dayjs().valueOf()).meta({
    title: 'Last edited at',
  }),
  note: UserNoteNoteSchema.meta({ title: 'Note' }),
  priorityDatetime: UserNotePriorityDatetimeSchema.default(0).meta({ title: 'Prioritised at' }),
});
